import { Racer } from './Racer.js';
import { ItemBox } from './ItemBox.js';
import { Rocket } from './Rocket.js';
import { Explosion } from './Explosion.js';
import { PlantedTree } from './PlantedTree.js';
import { TRACK_CENTERLINE, TRACK_HALF_WIDTH, POSE_SEND_HZ, paletteForLane } from '../utils/constants.js';

// Physics tuning.
const NORMAL_SPEED = 12;          // m/s along the slope
const TUCK_MULTIPLIER = 1.4;       // 1.4× normal while crouched (Shift / Crouch button)
const STEER_YAW_RATE = 1.9;        // rad/s at full steer (snappier turn)
const LEAN_PER_STEER = 0.45;       // max visual lean angle (rad) at full steer
const LEAN_EASE_PER_SEC = 8;       // exponential approach rate toward target lean
const STRAIGHTEN_PER_SEC = 2.5;    // ease rate toward downhill when steer ≈ 0
const STEER_DEADZONE = 0.05;       // ignore tiny joystick offsets for straightening
const GRAVITY = -22;               // m/s^2 (positive down → vy decreases)
const HOP_VY = 7;                  // upward velocity from a tap-jump
const CHARGED_JUMP_VY = 11;        // upward velocity from a charged (held) jump
const CHARGED_FLIP_SEC = 1.0;      // duration of the 360 front-flip animation
const CHARGED_JUMP_HOLD_THRESHOLD = 0.18; // seconds — above this, a release fires a charged jump

// When the racer crosses a ramp footprint while grounded, they're launched
// with this upward velocity and a 360 front-flip plays during the airtime.
const RAMP_LAUNCH_VY = 13;
const RAMP_FLIP_SEC = 1.0;

// Maximum lateral distance from centerline before the player slams into a
// wall. Course walls sit at TRACK_HALF_WIDTH + 2; this leaves a small margin
// for the racer's body width so they don't visually clip into the wall.
const MAX_LATERAL = TRACK_HALF_WIDTH + 1.5;

// Hitting a course-side tree freezes the racer for STUN_DURATION_SEC, then
// disables further collisions with anything for OBSTACLE_GRACE_SEC so the
// player can move free of the tree without being immediately re-stunned.
const STUN_DURATION_SEC = 2.0;
const OBSTACLE_GRACE_SEC = 1.0;
const RACER_RADIUS = 0.6;

// Deceleration once the finish line is crossed (m/s²). Picked so the racer
// glides to a stop in roughly 1 second from full tuck speed.
const FINISH_DECEL = 18;

// Projects (x, z) onto the centerline polyline. Returns the closest point on
// the polyline, the signed lateral offset (positive = right of travel), and
// unit tangent/right vectors for the matched segment. For points before the
// start or after the end, the projection clamps to the first/last segment so
// the corridor still has well-defined sides outside the racing zone.
function projectOntoCenterline(x, z) {
  const pts = TRACK_CENTERLINE;
  let segIndex = 0;
  if (z > pts[0].z) {
    segIndex = 0;
  } else if (z < pts[pts.length - 1].z) {
    segIndex = pts.length - 2;
  } else {
    for (let i = 0; i < pts.length - 1; i++) {
      if (z <= pts[i].z && z >= pts[i + 1].z) { segIndex = i; break; }
    }
  }
  const a = pts[segIndex];
  const b = pts[segIndex + 1];
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const segLen = Math.hypot(dx, dz);
  const tx = dx / segLen;
  const tz = dz / segLen;
  // Right vector (perpendicular in XZ, pointing right of direction of travel).
  const rx = -tz;
  const rz = tx;
  // Parameter along segment.
  let t = ((x - a.x) * tx + (z - a.z) * tz) / segLen;
  t = Math.max(0, Math.min(1, t));
  const cx = a.x + dx * t;
  const cz = a.z + dz * t;
  // Signed lateral offset (positive = right of travel direction).
  const lateral = (x - cx) * rx + (z - cz) * rz;
  return { cx, cz, lateral, tx, tz, rx, rz };
}

// Returns the surface Y at a given (x, z), interpolated along the centerline.
// We currently ignore x because the racing line stays smooth across the
// track's lateral extent — once we author per-vertex track meshes we'll
// use a proper height lookup instead.
function sampleSurfaceY(_x, z) {
  const pts = TRACK_CENTERLINE;
  if (z >= pts[0].z) return pts[0].y;
  if (z <= pts[pts.length - 1].z) return pts[pts.length - 1].y;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    if (z <= a.z && z >= b.z) {
      const t = (a.z - z) / (a.z - b.z);
      return a.y + (b.y - a.y) * t;
    }
  }
  return 0;
}

// Drives requestAnimationFrame. Each frame:
//   1. Reads InputManager intent.
//   2. Steps the local racer physics (TODO — placeholder for now).
//   3. Syncs remote racer meshes from server snapshots.
//   4. Asks the HUD to re-render its DOM bits.
//   5. Renders the scene.
//
// Physics is intentionally a stub here; we'll fill in gravity/steer/jump in
// step 2 of the staged build. The point of this scaffold is to get the
// websocket round-trip and render loop wired up first.
export class GameLoop {
  constructor({ renderer, scene, camera, gameState, input, hud, network }) {
    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;
    this.gameState = gameState;
    this.input = input;
    this.hud = hud;
    this.network = network;

    this.localRacer = null;
    this.remoteRacers = new Map(); // racerId -> Racer

    // Item-box meshes keyed by server-side id. Synced from race_update each
    // frame; their active flag determines material.
    this.itemBoxes = new Map();
    // Active rockets (homing projectiles). Updated every frame, removed
    // when expired or on hit.
    this.rockets = [];
    // Planted Christmas trees keyed by server-side id. Lives in activeItems
    // on race_update; despawn when the server stops broadcasting them.
    this.plantedTrees = new Map();
    // Short-lived explosion effects spawned on rocket impact.
    this.explosions = [];
    // The last item box we asked the server to grant — used to dedupe pickup
    // requests so we don't spam while standing in the same box.
    this._pendingPickupBoxId = null;

    // Obstacle colliders the player can hit (course-side trees).
    this.obstacles = scene.getObstacles?.() ?? [];
    // Jump pads the player launches off of.
    this.jumpPads = scene.getJumpPads?.() ?? [];
    // Tracks which jump pad the player most-recently launched off so we
    // don't re-trigger every frame while still in its footprint.
    this._lastJumpPadIdx = -1;
    // Phase the last frame saw, used to detect the racing → finished
    // transition so we only build the podium once per race.
    this._lastPhase = null;

    this.lastFrame = performance.now();
    this.poseSendAccumulator = 0;
    this.running = false;
  }

  start() {
    if (this.running) return;
    this.running = true;
    requestAnimationFrame(this._tick);
  }

  stop() {
    this.running = false;
  }

  _tick = (now) => {
    if (!this.running) return;
    const dt = Math.min(0.1, (now - this.lastFrame) / 1000);
    this.lastFrame = now;

    this._stepPhysics(dt);
    this._maybeSpawnIncomingExplosion();
    this._syncRemoteRacers();
    this._syncPlaceLabels();
    this._syncItemBoxes(now / 1000);
    this._syncPlantedTrees(now / 1000);
    this._maybeRequestPickup();
    this._updateRockets(dt);
    this._updateExplosions(dt);
    this._maybeSendPose(dt);
    this._maybeHandlePhaseChange();

    // Decay the touch-pan camera offset back to 0 each frame (no-op while
    // the user is actively panning).
    this.input.decayViewYawOffset?.(dt);

    // Camera: chase the racer normally; only frame the podium once every
    // racer has finished (phase becomes 'finished' on race_over). Showing
    // the podium boxes earlier left them visibly empty during the
    // waiting-for-AI gap, so we keep the chase cam on the local racer
    // until the ceremony is actually ready.
    if (this.gameState.phase === 'finished') {
      const focus = this.scene.getPodiumFocus?.();
      if (focus) this.camera.framePodium(focus, dt);
      else this.camera.follow(this.gameState.local.position, this.gameState.local.yaw, dt);
      this.scene.updatePodium?.(now / 1000);
    } else if (this.gameState.phase !== 'lobby') {
      // Spectators ride the chase cam of a specific racer. Space cycles
      // forward through the field. Falls back to the leader on first
      // spectate or if the current target leaves the room.
      if (this.gameState.isSpectator) {
        if (this.input.consumeSpaceEdge?.()) {
          this._spectatorCycleNext();
        }
        const targetId = this._currentSpectatorTargetId();
        const targetPose = targetId ? this.gameState.remote.get(targetId) : null;
        if (targetPose) {
          this.camera.follow(
            targetPose.position,
            targetPose.yaw,
            dt,
            this.input.getViewYawOffset?.() ?? 0,
          );
          this._spectatorActiveId = targetId;
        }
        this.hud.setSpectatorTarget?.(this._spectatorActiveId, this.gameState);
      } else {
        const mode = this.input.getViewMode?.() ?? 'chase';
        const yawOff = this.input.getViewYawOffset?.() ?? 0;
        if (mode === 'overhead') {
          this.camera.followOverhead(
            this.gameState.local.position,
            this.gameState.local.yaw,
            dt,
            yawOff,
          );
        } else {
          this.camera.follow(
            this.gameState.local.position,
            this.gameState.local.yaw,
            dt,
            yawOff,
          );
        }
      }
    }

    this.hud.update(this.gameState);
    this.renderer.render(this.scene.getScene(), this.camera.getCamera());

    requestAnimationFrame(this._tick);
  };

  _stepPhysics(dt) {
    if (this.gameState.phase !== 'racing') return;
    // Spectators are pure observers — no local racer mesh, no input, no
    // pose-send. The remote racer sync still runs in _syncRemoteRacers so
    // they see the field.
    if (this.gameState.isSpectator) return;

    const l = this.gameState.local;

    // Stun: planted face-first in the snow. No motion at all — applyHit
    // already zeroed velocity, set flipAngle = π, and forced grounded.
    // When the timer drains to 0 we trigger a jump-out: positive vy
    // (lifts the racer out of the snow) plus a half-revolution flip
    // animation that lands them on their board the right way up.
    if (l.stunSec > 0) {
      l.stunSec = Math.max(0, l.stunSec - dt);
      l.velocity.x = 0;
      l.velocity.z = 0;
      l.vy = 0;
      l.position.y = sampleSurfaceY(l.position.x, l.position.z);
      l.leanAngle *= Math.max(0, 1 - dt * 5);
      if (l.stunSec === 0) {
        // Jump-and-flip-back recovery. flipAngle is currently π; the
        // flip animation runs a full revolution per CHARGED_FLIP_SEC,
        // so 0.5s carries it π → 2π (= 0) and the rider lands upright.
        l.vy = CHARGED_JUMP_VY;
        l.grounded = false;
        l.flipRemainingSec = 0.5;
      }
      this._renderLocal();
      return;
    }

    // Finish: decelerate to a stop, ignore input, no further state changes.
    if (l.finished) {
      const speed = Math.hypot(l.velocity.x, l.velocity.z);
      const newSpeed = Math.max(0, speed - FINISH_DECEL * dt);
      const scale = speed > 0 ? newSpeed / speed : 0;
      l.velocity.x *= scale;
      l.velocity.z *= scale;
      l.position.x += l.velocity.x * dt;
      l.position.z += l.velocity.z * dt;
      l.position.y = sampleSurfaceY(l.position.x, l.position.z);
      l.leanAngle *= Math.max(0, 1 - dt * 5);
      this._renderLocal();
      return;
    }

    // Action button uses whatever we're holding. Consumed edge so a held
    // button only fires once per press.
    if (this.input.consumeItem?.()) {
      this._useHeldItem();
    }

    // Steering rotates yaw. (Yaw is the racing direction, *not* the visual
    // rotation; flipAngle is layered on top at render time.)
    const steer = this.input.getSteer();
    l.yaw += steer * STEER_YAW_RATE * dt;

    // Yaw cleanup vs the local downhill tangent: auto-straighten the racer
    // when they're not actively steering, AND clamp the result to ±81° of
    // downhill so they can never face uphill.
    {
      const cl = projectOntoCenterline(l.position.x, l.position.z);
      const downhillYaw = Math.atan2(cl.tx, -cl.tz);
      let rel = l.yaw - downhillYaw;
      // Normalise to (-π, π] so neither the ease nor the clamp wraps wrong.
      while (rel > Math.PI) rel -= 2 * Math.PI;
      while (rel < -Math.PI) rel += 2 * Math.PI;
      // No active steering → ease rel toward 0 so the racer goes straight
      // and doesn't drift laterally off whatever angle they last steered to.
      if (Math.abs(steer) < STEER_DEADZONE) {
        const k = 1 - Math.exp(-STRAIGHTEN_PER_SEC * dt);
        rel *= 1 - k;
      }
      const maxOff = Math.PI / 2 * 0.9; // ±81°
      rel = Math.max(-maxOff, Math.min(maxOff, rel));
      l.yaw = downhillYaw + rel;
    }

    // Carve lean: ease leanAngle toward -steer * LEAN_PER_STEER so the rider
    // visibly tilts into the turn. Negative coefficient because a +Z local
    // rotation tilts the body's top toward -X (left) — see Racer.setPose.
    const targetLean = -steer * LEAN_PER_STEER;
    const k = 1 - Math.exp(-LEAN_EASE_PER_SEC * dt);
    l.leanAngle += (targetLean - l.leanAngle) * k;

    // Tuck for 1.2× speed (matches the user-requested boost).
    l.tucking = this.input.isTucking();
    const forwardSpeed = NORMAL_SPEED * (l.tucking ? TUCK_MULTIPLIER : 1);

    // Horizontal velocity points "forward" along the racer's yaw, which in
    // our world means toward -Z (downhill direction at yaw=0).
    l.velocity.x = Math.sin(l.yaw) * forwardSpeed;
    l.velocity.z = -Math.cos(l.yaw) * forwardSpeed;

    // Jump trigger: a release event tells us how long Space was held.
    const heldFor = this.input.consumeJumpRelease();
    if (heldFor !== null && l.grounded) {
      if (heldFor >= CHARGED_JUMP_HOLD_THRESHOLD) {
        l.vy = CHARGED_JUMP_VY;
        l.flipRemainingSec = CHARGED_FLIP_SEC;
      } else {
        l.vy = HOP_VY;
        l.flipRemainingSec = 0;
      }
      l.grounded = false;
    }

    // Advance horizontal position.
    l.position.x += l.velocity.x * dt;
    l.position.z += l.velocity.z * dt;

    // Course wall collision: keep the racer inside the lateral corridor.
    // Project onto the centerline; if we've gone past MAX_LATERAL on either
    // side, snap back to the wall and remove the perpendicular velocity
    // component so the player slides along the wall instead of jittering.
    const proj = projectOntoCenterline(l.position.x, l.position.z);
    if (Math.abs(proj.lateral) > MAX_LATERAL) {
      const clamped = Math.sign(proj.lateral) * MAX_LATERAL;
      l.position.x = proj.cx + clamped * proj.rx;
      l.position.z = proj.cz + clamped * proj.rz;
      const vTangent = l.velocity.x * proj.tx + l.velocity.z * proj.tz;
      l.velocity.x = vTangent * proj.tx;
      l.velocity.z = vTangent * proj.tz;
    }

    // Tree collision: if the racer hit any obstacle and we're past the grace
    // window, set the stun timer and extend the grace so the player can move
    // away from the tree without being instantly re-stunned.
    const nowSec = performance.now() / 1000;
    if (nowSec >= l.ignoreObstacleUntil && this.obstacles) {
      for (const ob of this.obstacles) {
        const dx = l.position.x - ob.x;
        const dz = l.position.z - ob.z;
        const hitDist = ob.radius + RACER_RADIUS;
        if (dx * dx + dz * dz < hitDist * hitDist) {
          l.stunSec = STUN_DURATION_SEC;
          l.ignoreObstacleUntil = nowSec + STUN_DURATION_SEC + OBSTACLE_GRACE_SEC;
          // Nudge the racer to the edge of the collision circle so they
          // aren't visually clipped into the trunk during the stun.
          const dist = Math.hypot(dx, dz) || 1;
          l.position.x = ob.x + (dx / dist) * hitDist;
          l.position.z = ob.z + (dz / dist) * hitDist;
          l.velocity.x = 0;
          l.velocity.z = 0;
          break;
        }
      }
    }

    // Ramp launch: if grounded and the racer's horizontal position is inside
    // a jump pad's footprint (transforming into the pad's local frame),
    // launch them upward and trigger a front flip.
    if (l.grounded && this.jumpPads.length > 0) {
      for (let i = 0; i < this.jumpPads.length; i++) {
        const pad = this.jumpPads[i];
        if (i === this._lastJumpPadIdx) continue; // don't re-trigger same pad
        // Local space: cos/sin of pad's yaw rotates world → pad-local.
        const cosY = Math.cos(-pad.yaw);
        const sinY = Math.sin(-pad.yaw);
        const lx = (l.position.x - pad.x) * cosY - (l.position.z - pad.z) * sinY;
        const lz = (l.position.x - pad.x) * sinY + (l.position.z - pad.z) * cosY;
        if (Math.abs(lx) <= pad.halfWidth && Math.abs(lz) <= pad.halfLength) {
          l.vy = RAMP_LAUNCH_VY;
          l.flipRemainingSec = RAMP_FLIP_SEC;
          l.grounded = false;
          this._lastJumpPadIdx = i;
          break;
        }
      }
    }

    // Vertical integration: gravity while airborne, snap to surface on landing.
    const surfaceY = sampleSurfaceY(l.position.x, l.position.z);
    if (!l.grounded) {
      l.vy += GRAVITY * dt;
      l.position.y += l.vy * dt;
      if (l.position.y <= surfaceY) {
        l.position.y = surfaceY;
        l.vy = 0;
        l.grounded = true;
        l.flipAngle = 0;
        l.flipRemainingSec = 0;
        this._lastJumpPadIdx = -1;
      }
      l.velocity.y = l.vy;
    } else {
      // Glue to the surface so the rider follows the slope's elevation.
      l.position.y = surfaceY;
      l.vy = 0;
      l.velocity.y = 0;
    }

    // Advance the front-flip animation while airborne. Rotation is around
    // the racer's local X axis (lateral), so the rider rolls head-first
    // forward — what you'd see on a real snowboard front flip.
    if (l.flipRemainingSec > 0) {
      const step = (2 * Math.PI / CHARGED_FLIP_SEC) * dt;
      l.flipAngle = (l.flipAngle + step) % (2 * Math.PI);
      l.flipRemainingSec = Math.max(0, l.flipRemainingSec - dt);
    } else if (l.grounded) {
      l.flipAngle = 0;
    }

    // Advance lastCheckpoint as the racer passes each checkpoint volume's
    // Z value in order. The server cross-checks this when accepting a
    // finish_claim, so we must report it; the pose-send loop carries it
    // upstream automatically once we update it here.
    const cps = this.gameState.track?.checkpoints;
    if (cps && l.lastCheckpoint < cps.length) {
      const nextCp = cps[l.lastCheckpoint];
      if (l.position.z <= nextCp.center.z) {
        l.lastCheckpoint += 1;
      }
    }

    // Finish-line detection: when the racer crosses past the finish line's
    // Z value, latch the finished flag and notify the server. Decel + lock
    // is handled by the early-return at the top of _stepPhysics next frame.
    const finishZ = this.gameState.track?.finishLine?.center?.z;
    if (finishZ !== undefined && l.position.z < finishZ && !l.finished) {
      l.finished = true;
      this.network.send('finish_claim', {
        clientTime: Date.now(),
        lastCheckpoint: l.lastCheckpoint,
      });
    }

    this._renderLocal();
  }

  // Called by Game when returning to the lobby after a race ends. Removes
  // every racer mesh so the next race starts with a clean scene, clears
  // the podium, and resets the phase tracker so future racing→finished
  // transitions re-trigger the ceremony.
  resetForNewRace() {
    if (this.localRacer) {
      this.localRacer.destroy();
      this.localRacer = null;
    }
    for (const racer of this.remoteRacers.values()) racer.destroy();
    this.remoteRacers.clear();
    for (const box of this.itemBoxes.values()) box.destroy();
    this.itemBoxes.clear();
    for (const tree of this.plantedTrees.values()) tree.destroy();
    this.plantedTrees.clear();
    for (const rocket of this.rockets) rocket.destroy();
    this.rockets = [];
    this._pendingPickupBoxId = null;
    this._spectatorTargetId = null;
    this._spectatorActiveId = null;
    this.scene.clearPodium?.();
    this._lastPhase = null;
    this._lastJumpPadIdx = -1;
  }

  // Triggers one-shot effects on phase transitions (e.g. building the podium
  // when the race ends).
  _maybeHandlePhaseChange() {
    const phase = this.gameState.phase;
    if (phase === this._lastPhase) return;
    // Mid-cup transition: the previous race ended and a new race_start
    // flipped us back to 'countdown' without going through lobby. Clear
    // the racer meshes from the previous race so they don't sit at the
    // old finish-line positions while we spawn on the new track.
    if (this._lastPhase === 'racing' && phase === 'countdown') {
      this._clearRaceMeshes();
    }
    if (phase === 'finished') {
      const top3 = this._buildPodiumTop3();
      this.scene.showPodium?.(top3);
    }
    this._lastPhase = phase;
  }

  _clearRaceMeshes() {
    if (this.localRacer) {
      this.localRacer.destroy();
      this.localRacer = null;
    }
    for (const racer of this.remoteRacers.values()) racer.destroy();
    this.remoteRacers.clear();
    // Item boxes are positioned at construction time using the *active*
    // track's centerline. Mid-cup transitions swap centerlines, so meshes
    // left over from the previous race end up at stale positions on the
    // new track (the "boxes floating in the sky on map 2" bug). Drop them
    // so _syncItemBoxes recreates each one against the new surface.
    for (const box of this.itemBoxes.values()) box.destroy();
    this.itemBoxes.clear();
    for (const rocket of this.rockets) rocket.destroy();
    this.rockets = [];
    for (const tree of this.plantedTrees.values()) tree.destroy();
    this.plantedTrees.clear();
    for (const ex of this.explosions) ex.destroy();
    this.explosions = [];
    this._pendingPickupBoxId = null;
    this._lastJumpPadIdx = -1;
  }

  // Builds the top-3 entry list for Scene.showPodium. Prefers the cumulative
  // cup standings when the server includes them; falls back to single-race
  // results otherwise.
  _buildPodiumTop3() {
    const racers = this.gameState.racers || [];
    const lookup = (racerId) => {
      const racer = racers.find((x) => x.racerId === racerId);
      const lane = racer?.startLane ?? 0;
      return { racerId, palette: paletteForLane(lane) };
    };

    const cup = this.gameState.cupStandings;
    if (cup && cup.length > 0) {
      return [...cup]
        .sort((a, b) => a.cupPlace - b.cupPlace)
        .slice(0, 3)
        .map((s) => ({ ...lookup(s.racerId), place: s.cupPlace }));
    }

    const results = this.gameState.results || [];
    return results
      .filter((r) => r.place > 0)
      .sort((a, b) => a.place - b.place)
      .slice(0, 3)
      .map((r) => ({ ...lookup(r.racerId), place: r.place }));
  }

  // Adds/removes/updates ItemBox meshes to match the server-side state.
  _syncItemBoxes(timeSec) {
    const seen = new Set();
    for (const box of this.gameState.itemBoxes) {
      seen.add(box.id);
      let mesh = this.itemBoxes.get(box.id);
      if (!mesh) {
        // Snap the box to the actual racing surface at its (x, z) so it
        // sits on the track even if the server's authored Y drifts from
        // the centerline. The constructor then adds a small hover on top.
        const surfY = sampleSurfaceY(box.position.x, box.position.z);
        mesh = new ItemBox(this.scene.getScene(), {
          x: box.position.x,
          y: surfY,
          z: box.position.z,
        });
        this.itemBoxes.set(box.id, mesh);
      }
      mesh.setActive(!!box.active);
      mesh.update(timeSec);
    }
    // Despawn meshes the server has stopped reporting.
    for (const [id, mesh] of this.itemBoxes) {
      if (!seen.has(id)) {
        mesh.destroy();
        this.itemBoxes.delete(id);
      }
    }
  }

  // Adds/removes PlantedTree meshes to match server-side activeItems.
  _syncPlantedTrees(timeSec) {
    const seen = new Set();
    for (const item of this.gameState.activeItems) {
      if (item.itemType !== 'tree') continue;
      seen.add(item.id);
      let mesh = this.plantedTrees.get(item.id);
      if (!mesh) {
        // Snap the tree base to the racing surface so a tree planted on a
        // steep section still sits flush with the snow.
        const surfY = sampleSurfaceY(item.position.x, item.position.z);
        mesh = new PlantedTree(this.scene.getScene(), {
          x: item.position.x,
          y: surfY,
          z: item.position.z,
        });
        this.plantedTrees.set(item.id, mesh);
      }
      mesh.update(timeSec);
    }
    for (const [id, mesh] of this.plantedTrees) {
      if (!seen.has(id)) {
        mesh.destroy();
        this.plantedTrees.delete(id);
      }
    }
  }

  // Returns the racerId the spectator camera is currently targeting. If
  // the previously-selected target has dropped from the race (rare), we
  // fall back to the leader so the camera doesn't strand on empty space.
  _currentSpectatorTargetId() {
    const racers = this.gameState.racers || [];
    if (racers.length === 0) return null;
    const ids = racers.map((r) => r.racerId);
    if (this._spectatorTargetId && ids.includes(this._spectatorTargetId)) {
      return this._spectatorTargetId;
    }
    // No selection yet → start on the leader if we know who that is,
    // otherwise the first racer in the field.
    const leader = this.gameState.standings?.[0];
    this._spectatorTargetId = (leader && ids.includes(leader)) ? leader : ids[0];
    return this._spectatorTargetId;
  }

  // Advances the spectator target by one slot in the race field. Wraps
  // around so repeated Space presses cycle through everyone.
  _spectatorCycleNext() {
    const racers = this.gameState.racers || [];
    if (racers.length === 0) return;
    const ids = racers.map((r) => r.racerId);
    const i = ids.indexOf(this._currentSpectatorTargetId());
    this._spectatorTargetId = ids[(i + 1) % ids.length];
  }

  // Asks the server for a pickup if the local racer is overlapping any
  // active box. Deduped via _pendingPickupBoxId so we send at most one
  // request per box per occupancy. Holding an item doesn't skip the
  // pickup — the new item replaces the old one (handled server-side).
  _maybeRequestPickup() {
    if (this.gameState.phase !== 'racing') return;
    const l = this.gameState.local;
    let nearest = null;
    // 3m XZ radius — tight enough that grabbing a box feels deliberate
    // (you have to actually ride through it) but not so tight that a
    // slight lateral wobble misses. Y is intentionally ignored.
    const pickupRSq = 9;
    for (const box of this.gameState.itemBoxes) {
      if (!box.active) continue;
      const dx = l.position.x - box.position.x;
      const dz = l.position.z - box.position.z;
      if (dx * dx + dz * dz < pickupRSq) {
        nearest = box;
        break;
      }
    }
    if (!nearest) {
      this._pendingPickupBoxId = null;
      return;
    }
    if (this._pendingPickupBoxId === nearest.id) return;
    this._pendingPickupBoxId = nearest.id;
    this.network.send('item_pickup_request', { boxId: nearest.id });
  }

  // Routes an item-use press to the appropriate per-item handler. All paths
  // notify the server via item_use so it can clear/validate the held slot.
  _useHeldItem() {
    const item = this.gameState.heldItem;
    if (!item) return;
    const l = this.gameState.local;
    this.network.send('item_use', {
      itemType: item,
      originX: l.position.x,
      originY: l.position.y,
      originZ: l.position.z,
      headingX: Math.sin(l.yaw),
      headingY: 0,
      headingZ: -Math.cos(l.yaw),
    });
    if (item === 'rocket') {
      this._fireRocket();
    } else if (item === 'shield') {
      // Server confirms via race_update.shielded; we also pop the local
      // bubble immediately for instant feedback.
      this.gameState.local.shieldUntilMs = Date.now() + 15000;
      this.gameState.heldItem = '';
    } else if (item === 'tree') {
      // Server picks where it lands and broadcasts via activeItems. Just
      // clear our slot.
      this.gameState.heldItem = '';
    }
  }

  // Spawns a homing rocket aimed at the closest racer ahead of the local
  // player along the racing line, then clears the held item.
  _fireRocket() {
    const me = this.gameState.local;
    let bestId = null;
    let bestDistSq = Infinity;
    for (const [id, pose] of this.gameState.remote) {
      // "Ahead" along the course means a smaller (more negative) Z value
      // since the course descends along -Z.
      if (pose.position.z >= me.position.z) continue;
      const dx = pose.position.x - me.position.x;
      const dz = pose.position.z - me.position.z;
      const d = dx * dx + dz * dz;
      if (d < bestDistSq) {
        bestDistSq = d;
        bestId = id;
      }
    }
    if (!bestId) return; // no one ahead to target
    this.rockets.push(new Rocket(this.scene.getScene(), me.position, bestId));
    this.gameState.heldItem = '';
  }

  // Advances each in-flight rocket. On a hit, spawn an explosion at the
  // impact point and tell the server (which stuns the target). On expiry,
  // drop the rocket.
  _updateRockets(dt) {
    for (let i = this.rockets.length - 1; i >= 0; i--) {
      const rocket = this.rockets[i];
      const targetPose = this.gameState.remote.get(rocket.targetId);
      const result = rocket.update(dt, targetPose);
      if (result === 'hit') {
        this.network.send('item_hit_claim', {
          itemType: 'rocket',
          targetId: rocket.targetId,
          clientTime: Date.now(),
        });
        this.explosions.push(new Explosion(this.scene.getScene(), rocket.group.position));
      }
      if (result !== null) {
        rocket.destroy();
        this.rockets.splice(i, 1);
      }
    }
  }

  // While the local racer has finished but the server hasn't called the
  // race yet, paint a floating "1ST/2ND/3RD/4TH" above every racer's head
  // (sourced from gameState.standings). Hide them in every other phase.
  _syncPlaceLabels() {
    const showLabels =
      this.gameState.local?.finished &&
      this.gameState.phase !== 'finished' &&
      this.gameState.phase !== 'lobby';
    const standings = this.gameState.standings || [];

    if (!showLabels) {
      this.localRacer?.hidePlaceLabel();
      for (const racer of this.remoteRacers.values()) racer.hidePlaceLabel();
      return;
    }

    const placeOf = (racerId) => {
      const idx = standings.indexOf(racerId);
      return idx >= 0 ? idx + 1 : 0;
    };
    this.localRacer?.setPlaceLabel(placeOf(this.gameState.racerId));
    for (const [id, racer] of this.remoteRacers) {
      racer.setPlaceLabel(placeOf(id));
    }
  }

  // When a server-side rocket lands on us (AI fire), GameState.applyHit
  // queues the impact position; spawn the explosion VFX here so the user
  // can see what hit them, then clear the queue.
  _maybeSpawnIncomingExplosion() {
    const at = this.gameState.local.pendingExplosionAt;
    if (!at) return;
    this.explosions.push(new Explosion(this.scene.getScene(), at));
    this.gameState.local.pendingExplosionAt = null;
  }

  // Animates and reaps active explosions.
  _updateExplosions(dt) {
    for (let i = this.explosions.length - 1; i >= 0; i--) {
      const ex = this.explosions[i];
      if (ex.update(dt)) {
        ex.destroy();
        this.explosions.splice(i, 1);
      }
    }
  }

  // Lazily creates the local racer mesh and updates its pose from state.
  _renderLocal() {
    const l = this.gameState.local;
    if (!this.localRacer) {
      const lane = this._laneFor(this.gameState.racerId);
      this.localRacer = new Racer(this.scene.getScene(), paletteForLane(lane));
    }
    const buried = l.stunSec > 0;
    this.localRacer.setPose(l.position, l.yaw, l.flipAngle, l.leanAngle, l.tucking, buried);
    this.localRacer.setStunned(buried);
    this.localRacer.setShielded(l.shieldUntilMs > Date.now());
  }

  // Look up a racer's startLane from the race_start payload.
  _laneFor(racerId) {
    const racer = this.gameState.racers?.find((r) => r.racerId === racerId);
    return racer?.startLane ?? 0;
  }

  _syncRemoteRacers() {
    // Add/move meshes for each remote racer the server told us about.
    const seen = new Set();
    for (const [id, pose] of this.gameState.remote) {
      seen.add(id);
      let racer = this.remoteRacers.get(id);
      if (!racer) {
        racer = new Racer(this.scene.getScene(), paletteForLane(this._laneFor(id)));
        this.remoteRacers.set(id, racer);
      }
      // TODO: interpolate by INTERP_DELAY_MS for smoothness.
      const buried = !!pose.stunned;
      racer.setPose(
        pose.position,
        pose.yaw,
        buried ? Math.PI : 0,
        0,
        !!pose.tucking,
        buried,
      );
      racer.setStunned(buried);
      racer.setShielded(!!pose.shielded);
    }
    // Despawn any racers that the server has stopped reporting.
    for (const [id, racer] of this.remoteRacers) {
      if (!seen.has(id)) {
        racer.destroy();
        this.remoteRacers.delete(id);
      }
    }
  }

  _maybeSendPose(dt) {
    if (this.gameState.phase !== 'racing') return;
    if (this.gameState.isSpectator) return;
    this.poseSendAccumulator += dt;
    const period = 1 / POSE_SEND_HZ;
    if (this.poseSendAccumulator < period) return;
    this.poseSendAccumulator = 0;

    const l = this.gameState.local;
    this.network.send('racer_pose', {
      position: l.position,
      velocity: l.velocity,
      yaw: l.yaw,
      pitch: l.pitch,
      roll: l.roll,
      grounded: l.grounded,
      tucking: l.tucking,
      stunned: l.stunSec > 0,
      trickState: l.trickState,
      heldItem: this.gameState.heldItem,
      lastCheckpoint: l.lastCheckpoint,
      clientTime: Date.now(),
    });
  }
}
